import { Component, type ErrorInfo, type ReactNode } from "react";
import { Banner, BlockStack, Card, Page, Text } from "@shopify/polaris";

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console is acceptable in the browser shell; the backend has pino.
    console.error("dashboard_error_boundary", { error, info });
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <Page title="Something went wrong">
          <BlockStack gap="400">
            <Banner tone="critical" title="The dashboard crashed">
              <p>Reload the page to try again. If this persists, please contact support.</p>
            </Banner>
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  Error detail
                </Text>
                <Text as="p" variant="bodyMd">
                  {this.state.error.message}
                </Text>
              </BlockStack>
            </Card>
          </BlockStack>
        </Page>
      );
    }
    return this.props.children;
  }
}
